/**
 * @render react
 * @name Navigation
*/

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withPolaris } from 'Components/protons/Polaris';

import { Icon } from 'Components/atoms/Icon';
import { Link } from 'Components/molecules/Link';

/**
 * NavigationItem component to be used by Navigation component
 *
 * @param {Array} item — Object containing title, url, children keys.
 * @param {string} subMenuIcon — The icon to be used for subenus.
 * @param {string} viewAllLabel — The label when a menu item is promoted
 * @param {string} action — Action for sub menus to be displayed. Either hover or click
 * @param {boolean} activeWildcard — Checks whether we want exact or wildcard match for the active menu item
 * @param {string} subMenuClickBreakpoint — Breakpoint for sub menus with children to be clickable
 * @param {function} onClickCallback — Callback when a navigation item is clicked
 * @param {Object} context — Extra information coming from Polaris provider.
 */
const NavigationItem = ({
  item,
  extraSubMenuContent,
  subMenuIcon,
  viewAllLabel,
  onClickCallback,
  action,
  activeWildcard,
  showSubMenuTitle,
  subMenuClickBreakpoint,
  context: { cssPrefix, platform },
  parentRefCallback,
  isMobileOrTablet,
  breakpoint,
}) => {
  const pathname = typeof window !== 'undefined' && window.location.pathname;
  /**
   * To know when a menu item is opened
   */
  const [isOpen, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Gets currently active path. Note this is a wildcard match, so it's possible
  // to have multiple paths return true.
  const isActivePath = (url) => {
    // If not, we try to deduce the active path, wildcard
    if (activeWildcard) {
      return pathname !== '/' && url.indexOf(pathname) === 0;
    }

    // Exact match
    return pathname !== '/' && url === pathname;
  };

  // Check if item has any children
  const hasChildren = item.children && item.children.length > 0;

  // Check if the parent menu item is active (this happens when the child menu item is active)
  let activeParent = false;
  if (hasChildren) {
    const activeIndex = item.children.findIndex((child) => {
      if (child && child.url) {
        return isActivePath(child.url);
      }

      return false;
    });

    if (activeIndex !== -1) {
      activeParent = true;
    }
  }
  useEffect(() => {
    if (action !== 'hover') {
      setIsHovered(false);
    }
  }, [action]);

  /**
   * When a menu item with children is clicked
   */
  const onClick = (event) => {
    if (hasChildren) {
      if (breakpoint === 'mobile' || breakpoint === subMenuClickBreakpoint || action === 'click') {
        event.preventDefault();
        setOpen(!isOpen);
      }
    } else if (onClickCallback) {
      onClickCallback();
    }
  };
  const parentRef = useRef(null);
  // Call the parentRefCallback when the ref is set
  if (parentRefCallback) {
    parentRefCallback(parentRef.current);
  }

  const handleMouseEnter = () => {
    if (isMobileOrTablet) return; // Prevent mouse enter on mobile/tablet
    if (action === 'hover' && !isOpen) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isMobileOrTablet) return; // Prevent mouse leave on mobile/tablet
    setIsHovered(false);
  };
  return (
    <li
      ref={parentRef}
      className={classNames({
        [`${cssPrefix}__navigation--item`]: true,
        '-has-sub-menu': hasChildren,
        '-is-active-parent': activeParent,
        '-is-open': isOpen,
        '-no-link': !item.url,
        '-secondary': item.type === 'headersecondary',
      })}
      key={item.id}
      onClick={
        item.url || platform === 'amp' ? undefined : item.onClick || onClick
      }
      onKeyDown={
        item.url || platform === 'amp' ? undefined : item.onClick || onClick
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Link
        href={item.url}
        extraClassNames={{
          ...item.extraClasses,
          '-is-active': isActivePath(item.url),
        }}
        target={item.target}
        onClick={item.onClick || onClick}
        rel={item.rel}
      >
        <span>
          {item.title}
        </span>

        {hasChildren && (
          <span className={`${cssPrefix}__navigation--item-icon`}>
            {
              subMenuIcon ? <Icon icon={subMenuIcon} /> : '↓'
            }
          </span>
        )}
      </Link>

      {hasChildren && (
        <div className="-sub-menu" aria-hidden={isOpen ? 'false' : 'true'}>
          <div className="-sub-menu-inner">
            {showSubMenuTitle && (
              <Link
                href={item.url}
                extraClassNames={{
                  [`${cssPrefix}__navigation--item-title`]: true,
                }}
              >
                {item.title}
              </Link>
            )}
            <ul className="-sub-menu-list">
              {item.children.map((child, index) => (
                <li key={child.id || index}>
                  <Link
                    href={child.url}
                    extraClassNames={{
                      ...child.extraClasses,
                      '-is-active': isActivePath(child.url),
                    }}
                    onClick={child.onClick || onClickCallback}
                    target={child.target}
                    rel={child.rel}
                  >
                    <span>{child.title}</span>
                  </Link>
                </li>
              ))}
              {item.viewAll && (
                <li key="sub-menu-list--view-all">
                  <Link
                    href={item.url}
                    extraClassNames={{
                      '-sub-menu--view-all': true,
                      ...item.extraClasses,
                      '-is-active': isActivePath(item.url),
                    }}
                    onClick={item.onClick || onClickCallback}
                    target={item.target}
                    rel={item.rel}
                  >
                    {`${viewAllLabel} ${item.title}`}
                  </Link>
                </li>
              )}
            </ul>
            {((!isOpen && isHovered && action === 'hover')
              || (isOpen && breakpoint !== 'mobile' && breakpoint !== 'tablet' && action === 'click'))
              && extraSubMenuContent}
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * Valid prop types
 */
NavigationItem.propTypes = {
  item: PropTypes.shape(),
  context: PropTypes.shape(),
  onClickCallback: PropTypes.func,
  subMenuIcon: PropTypes.string,
  viewAllLabel: PropTypes.string,
  action: PropTypes.string,
  showSubMenuTitle: PropTypes.bool,
  subMenuClickBreakpoint: PropTypes.string,
  activeWildcard: PropTypes.bool,
  extraSubMenuContent: PropTypes.element,
  parentRefCallback: PropTypes.func,
  isMobileOrTablet: PropTypes.bool,
  breakpoint: PropTypes.string,
};

/**
 * Default props
 */
NavigationItem.defaultProps = {
  item: null,
  context: null,
  subMenuIcon: null,
  onClickCallback: null,
  action: 'hover',
  activeWildcard: true,
  showSubMenuTitle: false,
  subMenuClickBreakpoint: 'mobile',
  viewAllLabel: 'View all',
  extraSubMenuContent: null,
  parentRefCallback: null,
  isMobileOrTablet: false,
  breakpoint: 'desktop',
};

export default compose(withPolaris)(NavigationItem);
